import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";
import * as authApi from "../api/auth";
import { DEFAULT_CONFIGURATION } from "../api/configuration";
import {
  beginTerminalSession,
  clearToken,
  getToken,
  writeEmployeeRecord,
} from "../lib/auth";
import { clearCachedUserInfo, writeCachedUserInfo } from "../lib/userInfoCache";
import {
  blockedLicenseNotice,
  readOnlyLicenseNotice,
  type LicenseBlockedDetail,
  type LicenseNotice,
  type PendingLicenseNotice,
} from "../lib/licenseNotice";

// Types
export interface AuthState {
  token: string | null;
  user: authApi.UserInfoResponse | null;
  roles: authApi.RoleOut[];
  permissions: authApi.PermissionOut[];
  restaurantAccesses: boolean;
  access: boolean; // Дали потребителят е в тестов режим (от user_sessions)
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  // Login response data
  userId: number | null;
  username: string | null;
  names: string | null;
  personal: boolean | null;
  restaurantId: number | null;
  // Предупреждението за лиценза, чакащо „Разбрах" (LicenseNoticeHost) — от
  // отговора на входа или от отказа след гратисния период.
  licenseNotice: PendingLicenseNotice | null;
}

const initialState: AuthState = {
  token: null,
  user: null,
  roles: [],
  permissions: [],
  restaurantAccesses: false,
  access: false,
  status: "idle",
  error: null,
  userId: null,
  username: null,
  names: null,
  personal: null,
  restaurantId: null,
  licenseNotice: null,
};

/** Всеки успешен вход решава наново: предупреждение от BE или нищо. */
const pendingFromLogin = (
  notice: LicenseNotice | null | undefined,
): PendingLicenseNotice | null => (notice ? { notice, blocked: false } : null);

/**
 * Стойността, с която `login` отказва, когато отговорът НЯМА `detail`
 * (мрежова грешка, недостижима каса в локален режим). Не е текст за
 * показване — `Login.tsx` го различава от истинския `detail` на сървъра, за
 * да не покаже английски низ вместо превода (docs/edge.md §Вход без интернет).
 */
export const LOGIN_FAILED_FALLBACK = "Login failed";

// Async Thunks
export const login = createAsyncThunk(
  "auth/login",
  async (credentials: authApi.LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: {
            detail?: string | authApi.FullCapacityError;
          };
        };
      };

      if (err.response?.status === 409 && err.response?.data?.detail) {
        return rejectWithValue(err);
      }

      return rejectWithValue(
        err.response?.data?.detail || LOGIN_FAILED_FALLBACK,
      );
    }
  },
);

export const loginWithCode = createAsyncThunk(
  "auth/loginWithCode",
  async (credentials: authApi.LoginWithCodeRequest, { rejectWithValue }) => {
    try {
      const response = await authApi.loginWithCode(credentials);
      return response;
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { detail?: string | Array<{ msg: string }> } };
      };
      const detail = err.response?.data?.detail;

      // Handle both string and array error formats from backend
      if (Array.isArray(detail)) {
        return rejectWithValue(detail);
      }

      return rejectWithValue(detail || "Login with code failed");
    }
  },
);

/**
 * Защо `GET /user/info` се провали — извикващите (гейтът на общия терминал)
 * трябва да различават мрежова грешка (облакът го няма, самоличността от
 * кеша остава) от отказ на сървъра (401/403 = мъртъв токен).
 */
export interface UserInfoFailure {
  message: string;
  /** HTTP статусът на отказа; null = мрежова грешка без отговор. */
  httpStatus: number | null;
  /** Заявката е тръгнала към касата (локален режим, docs/edge.md) — 401
   *  оттам не е присъда за облачната сесия и не трие кеша. */
  edgeLocal: boolean;
}

export const isUserInfoNetworkFailure = (failure: unknown): boolean =>
  !!failure &&
  typeof failure === "object" &&
  "httpStatus" in failure &&
  (failure as UserInfoFailure).httpStatus === null;

function describeUserInfoFailure(error: unknown): UserInfoFailure {
  if (isAxiosError(error)) {
    const detail: unknown = error.response?.data?.detail;
    return {
      message:
        typeof detail === "string" && detail
          ? detail
          : "Failed to fetch user info",
      httpStatus: error.response ? error.response.status : null,
      edgeLocal: error.config?.edgeLocal === true,
    };
  }
  return {
    message: "Failed to fetch user info",
    httpStatus: null,
    edgeLocal: false,
  };
}

export const fetchUserInfo = createAsyncThunk<
  authApi.UserInfoResponse,
  { skipAuthRedirect?: boolean } | undefined,
  { state: { auth: AuthState }; rejectValue: UserInfoFailure }
>("auth/fetchUserInfo", async (options, { getState, rejectWithValue }) => {
  // Токенът, с който ТРЪГВА заявката (същият избор като axios интерсептора):
  // кешът на самоличността се пише под него — дори междувременно на общия
  // терминал да е влязъл друг служител, отговорът е за този токен.
  const token = getToken(getState().auth.token);
  try {
    const response = await authApi.getCurrentUserInfo(options);
    if (token) writeCachedUserInfo(token, response);
    return response;
  } catch (error: unknown) {
    const failure = describeUserInfoFailure(error);
    // Само доказано мъртъв токен (401/403 от ОБЛАКА) трие кеша; мрежова
    // грешка и отказ на касата го оставят — lib/userInfoCache.ts.
    if (
      !failure.edgeLocal &&
      (failure.httpStatus === 401 || failure.httpStatus === 403)
    ) {
      clearCachedUserInfo(token);
    }
    return rejectWithValue(failure);
  }
});

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.logout();
      return response;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err.response?.data?.detail || "Failed to logout");
    }
  },
);

/** Отговорът на `GET /user/info` (жив или от кеша) → полетата на сесията. */
function applyUserInfo(state: AuthState, info: authApi.UserInfoResponse) {
  state.status = "succeeded";
  state.user = info;
  state.roles = info.roles;
  state.permissions = info.permissions;
  state.restaurantAccesses = info.restaurant_accesses;
  state.access = info.access;
  state.personal = info.personal;
  state.userId = info.id;
  state.username = info.username;
  state.names = info.names;
}

// Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Директно обновяване на access полето (от confirm/deny-access mutation)
    setAccess: (state, action: { payload: boolean }) => {
      state.access = action.payload;
    },
    // Хидратиране от кеша на самоличността (lib/userInfoCache.ts) ПРЕДИ
    // първия рендер — личен акаунт при старт (main.tsx). Състоянието е
    // „succeeded", за да не отскочат route guard-овете, докато
    // fetchUserInfo опреснява на заден план (или пада без интернет).
    hydrateUserInfo: (
      state,
      action: PayloadAction<authApi.UserInfoResponse>,
    ) => {
      applyUserInfo(state, action.payload);
    },
    // Възстановяване на служителския токен от записа в localStorage (вика се
    // само от гейта на общия терминал — при refresh, при осиновяване на
    // служител, влязъл от съседен прозорец, при смяна на режима облак/каса).
    // С кеширана самоличност за този токен ролите идват веднага, иначе — от
    // последващия fetchUserInfo.
    restoreEmployeeSession: (
      state,
      action: PayloadAction<{
        token: string;
        cachedUserInfo: authApi.UserInfoResponse | null;
      }>,
    ) => {
      state.token = action.payload.token;
      if (action.payload.cachedUserInfo) {
        applyUserInfo(state, action.payload.cachedUserInfo);
      }
    },
    // Слива записана настройка (отговорът на PUT) в `user.configuration`, за
    // да не се презарежда целият /user/info след всяка промяна.
    patchConfiguration: (
      state,
      action: PayloadAction<Partial<authApi.UserInfoResponse["configuration"]>>,
    ) => {
      if (!state.user) return;
      // Default-ите отдолу: ако user-ът е от BE без `configuration` (стар
      // деплой), частичният отговор да не остави другите полета undefined.
      state.user.configuration = {
        ...DEFAULT_CONFIGURATION,
        ...state.user.configuration,
        ...action.payload,
      };
    },
    // Входът е отказан, защото гратисният период на лиценза е минал (403
    // `license_expired`) — показва се същият модал в „спрян" вид.
    showLicenseBlocked: (state, action: PayloadAction<LicenseBlockedDetail>) => {
      state.licenseNotice = blockedLicenseNotice(action.payload);
    },
    /** Модалът „Само преглед" (`lib/licenseReadOnly.ts`); payload = датата на изтичане. */
    showLicenseReadOnly: (state, action: PayloadAction<string>) => {
      state.licenseNotice = readOnlyLicenseNotice(action.payload);
    },
    dismissLicenseNotice: (state) => {
      state.licenseNotice = null;
    },
    switchToNonPersonal: (state) => {
      // Връщане към акаунта за общ достъп (изход на служителя → пак пин).
      // Чистим и ролите/правата/user, за да не остане стар служителски контекст
      // и SharedAccountGate да поиска нов пин.
      //
      // НЕ пипа storage: записът за служителя вече е ОБЩ за всички прозорци и
      // безусловното му триене оттук би изтрило живия запис на съседа. Всеки
      // извикващ сам решава дали да го изчисти (човешки „Изход" → безусловно;
      // автоматично сваляне → compare-and-clear в sessionRecovery).
      state.personal = false;
      state.token = null;
      state.userId = null;
      state.username = null;
      state.names = null;
      state.user = null;
      state.roles = [];
      state.permissions = [];
      // ЗАДЪЛЖИТЕЛНО: без нулиране на access ПИН екранът застава с access=true и
      // бутонът „Изход" поема клона „изход от тестов режим" вместо изхода с
      // парола — тоест терминалът остава без работещ авариен изход.
      state.access = false;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.token = action.payload.access_token;
        state.userId = action.payload.user_id;
        state.username = action.payload.username;
        state.names = action.payload.names;
        state.personal = action.payload.personal;
        state.restaurantId = action.payload.restaurant_id;
        state.licenseNotice = pendingFromLogin(action.payload.license_notice);
        // Токенът, видът на акаунта и името за ПИН екрана (Бар/Кухня…) се
        // записват от едно място, а служителят по дефиниция започва на чисто —
        // нов вход с парола не бива да възкреси запис, вързан за стария X.
        beginTerminalSession({
          token: action.payload.access_token,
          personal: action.payload.personal,
          sharedName:
            action.payload.personal === false
              ? action.payload.names || action.payload.username || null
              : null,
        });
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });

    // Login with code
    builder
      .addCase(loginWithCode.pending, (state) => {
        // state.status = "loading";
        state.error = null;
      })
      .addCase(loginWithCode.fulfilled, (state, action) => {
        // state.status = "succeeded";
        // Пазим служителския токен в localStorage (общ за всички табове и
        // прозорци на терминала), за да не връща refresh обратно към ПИН екрана
        // и за да не могат два прозореца да държат двама различни служители.
        writeEmployeeRecord(action.payload.access_token);
        state.token = action.payload.access_token;
        state.userId = action.payload.user_id;
        state.username = action.payload.username;
        state.names = action.payload.names;
        state.personal = action.payload.personal;
        state.restaurantId = action.payload.restaurant_id;
        state.licenseNotice = pendingFromLogin(action.payload.license_notice);
      })
      .addCase(loginWithCode.rejected, (state, action) => {
        // state.status = "failed";
        state.error = action.payload as string;
      });

    // Fetch user info
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        // Хидратирана (или вече заредена) самоличност се опреснява на заден
        // план — „loading" би показал спинъра на AuthorizedRoute върху
        // работещ екран. Без данни спинърът е точно каквото трябва.
        if (!state.user) state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        applyUserInfo(state, action.payload);
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.error = action.payload?.message ?? "Failed to fetch user info";
        // Провалено опресняване НЕ сваля самоличността (мрежова грешка без
        // интернет, отказ на касата): ролите остават, status е „succeeded".
        // Доказано мъртъв токен (401 от облака) стига до handleAuthFailure
        // по своя път и оттам до ПИН/изход.
        state.status = state.user ? "succeeded" : "failed";
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.status = "loading";
      })
      .addCase(logout.fulfilled, (state) => {
        clearToken();
        state.token = null;
        state.user = null;
        state.roles = [];
        state.permissions = [];
        state.restaurantAccesses = false;
        state.access = false;
        state.userId = null;
        state.username = null;
        state.personal = null;
        state.restaurantId = null;
        state.status = "idle";
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        // Дори при грешка, изчистваме локалния state
        clearToken();
        state.token = null;
        state.user = null;
        state.roles = [];
        state.permissions = [];
        state.restaurantAccesses = false;
        state.access = false;
        state.userId = null;
        state.username = null;
        state.personal = null;
        state.restaurantId = null;
        state.status = "idle";
        state.error = null;
      });
  },
});

export const {
  switchToNonPersonal,
  setAccess,
  hydrateUserInfo,
  restoreEmployeeSession,
  patchConfiguration,
  showLicenseBlocked,
  showLicenseReadOnly,
  dismissLicenseNotice,
} = authSlice.actions;

// Админският акаунт е с фиксирано user id 1. ЕДИНСТВЕНОТО място с тази
// проверка — преизползвай селектора вместо да я дублираш.
export const selectIsAdminUser = (state: { auth: AuthState }) =>
  state.auth.userId === 1;

export default authSlice.reducer;
